/**
 * Télémétrie du défaut de rendu Canvas #1405.
 *
 * Le contenu juridique ne franchit jamais cette frontière : PostHog reçoit des
 * identifiants techniques, des compteurs et une raison fournisseur fermée. Les
 * textes ne servent que localement à reconnaître deux fragments peints
 * identiques, puis sont jetés avant la capture.
 */

import posthog from 'posthog-js';
import type { CanvasActionName } from '@hector/shared-types';
import type { IssueDesRefusPassagers } from '../state/canvas-agent/refusPassager';

const EVENT_NAME = 'canvas_write_diagnostic';
const LOG_PREFIX = '[DEBUG-CANVAS-WRITE-1405]';
const AGENT_TRANSPORT_EVENT_NAME = 'canvas_agent_transport_diagnostic';
const AGENT_TRANSPORT_LOG_PREFIX = '[DEBUG-CANVAS-AGENT-TRANSPORT]';

interface CanvasDiagnosticContext {
  readonly conversationId: string;
  readonly documentId: string | null;
  readonly jobId: string | null;
  readonly mountEpoch: number;
}

interface CanvasWriteChunkDiagnostic extends CanvasDiagnosticContext {
  readonly operationId: string;
  readonly requestId?: string;
  readonly writeGeneration: number;
  readonly chunkIndex: number;
  readonly chunkCount: number;
  readonly chunkBlockCount: number;
  readonly attemptIndex: number;
  readonly cursorEmitted: number;
  readonly cursorConfirmed: number;
}

export type CanvasWriteDiagnostic =
  | (CanvasWriteChunkDiagnostic & {
      readonly stage: 'chunk_started';
    })
  | (CanvasWriteChunkDiagnostic & {
      readonly stage: 'chunk_completed' | 'chunk_timed_out';
      readonly durationMs: number;
    })
  | (CanvasWriteChunkDiagnostic & {
      readonly stage: 'transient_retry';
      readonly providerReason: string;
      readonly retryDelayMs: number;
    })
  | (CanvasDiagnosticContext & {
      readonly stage: 'writer_export_wait_started';
      /** Absent quand l'attente vient d'une tranche du flux de génération, qui n'a pas de requête d'agent. */
      readonly requestId?: string;
    })
  | (CanvasDiagnosticContext & {
      readonly stage: 'writer_export_wait_completed';
      readonly requestId?: string;
      readonly durationMs: number;
    })
  | (CanvasDiagnosticContext & {
      readonly stage: 'stream_completed';
      readonly streamBlockCount: number;
      readonly cursorEmitted: number;
      readonly cursorConfirmed: number;
      readonly writeInFlight: boolean;
    })
  | (CanvasDiagnosticContext & {
      readonly stage: 'render_integrity';
      readonly samplePhase: 'stream_completed' | 'writer_idle_stable';
      readonly paginationStable: boolean;
      readonly sampleCount: number;
      readonly modelBlockCount: number | null;
      readonly paintedPageCount: number;
      readonly paintedFragmentCount: number;
      readonly paintedUniqueBlockCount: number;
      readonly duplicatePaintedFragmentCount: number;
      readonly anomalyKind: CanvasPaintAnomaly;
    })
  | (CanvasDiagnosticContext & {
      readonly stage: 'superdoc_error';
      readonly errorSource: 'content' | 'exception';
      readonly signature: SignatureExceptionSuperDoc;
    });

/**
 * Ce qu'on garde d'une exception SuperDoc : sa NATURE, jamais son message.
 *
 * Sans elle, `superdoc_error` dit seulement « une exception a eu lieu » : le
 * 2026-10-05, deux dépassements de borne d'écriture (conversation `f0fb620a`)
 * suivaient chacun une exception de quelques secondes, et rien ne permettait de
 * dire laquelle — la capture console du Session Replay les avait perdues.
 */
export interface SignatureExceptionSuperDoc {
  /** Membre de l'union `SuperDocExceptionPayload`, reconnu par sa clé discriminante. */
  readonly payloadKind:
    | 'diagnostic' | 'store' | 'editor' | 'toolbar' | 'hyperlink' | 'collaboration' | 'unknown';
  readonly diagnosticCode: string | null;
  readonly diagnosticStage: string | null;
  readonly severity: string | null;
  readonly internalCode: string | null;
}

const SIGNATURE_INCONNUE: SignatureExceptionSuperDoc = {
  payloadKind: 'unknown',
  diagnosticCode: null,
  diagnosticStage: null,
  severity: null,
  internalCode: null,
};

/**
 * Un code est un identifiant technique court. `internalCode` n'est pas gouverné
 * par SuperDoc (« may change between versions without notice ») : cette forme
 * fermée est ce qui garantit qu'aucun texte d'acte ne franchit la frontière
 * PostHog par ce champ, quoi que l'éditeur y mette un jour.
 */
const FORME_DE_CODE = /^[A-Za-z0-9._:-]{1,80}$/;

function codeOuNull(valeur: unknown): string | null {
  return typeof valeur === 'string' && FORME_DE_CODE.test(valeur) ? valeur : null;
}

/**
 * Réduit un événement `onException` / `onContentError` à sa signature.
 *
 * `message` et `error` ne sont volontairement pas lus : un message de moteur de
 * rendu peut citer le contenu qu'il n'a pas su projeter.
 */
export function signerExceptionSuperDoc(evenement: unknown): SignatureExceptionSuperDoc {
  if (typeof evenement !== 'object' || evenement === null) return SIGNATURE_INCONNUE;
  const champs = evenement as Record<string, unknown>;
  if ('diagnosticCode' in champs) {
    return {
      payloadKind: 'diagnostic',
      diagnosticCode: codeOuNull(champs.diagnosticCode),
      diagnosticStage: codeOuNull(champs.diagnosticStage),
      severity: codeOuNull(champs.severity),
      internalCode: codeOuNull(champs.internalCode),
    };
  }
  // Mêmes discriminants que ceux documentés sur `SuperDocExceptionPayload`.
  const payloadKind = 'stage' in champs ? 'store'
    : 'code' in champs ? 'editor'
    : 'itemName' in champs ? 'toolbar'
    : 'source' in champs ? 'hyperlink'
    : 'collaborationReason' in champs ? 'collaboration'
    : 'unknown';
  return { ...SIGNATURE_INCONNUE, payloadKind };
}

type PostHogProperties = Record<string, string | number | boolean | null>;

/** Uniquement les valeurs de protocole nécessaires pour joindre le front au log backend. */
type CanvasAgentTransportContext = {
  readonly requestId: string;
  readonly tool: 'canvas_inspect' | 'canvas_perform_action';
  readonly action: 'inspect' | CanvasActionName;
  readonly mountEpoch?: number;
};

export type CanvasAgentTransportDiagnostic =
  | (CanvasAgentTransportContext & { readonly stage: 'request_received' })
  | (CanvasAgentTransportContext & {
      readonly stage: 'response_send_attempted';
      readonly envelopeOutcome: 'success' | 'failure';
      readonly durationMs: number;
    })
  /** Écriture refusée sans exécution : livrée trop longtemps après son envoi (#44, onglet gelé). */
  | (CanvasAgentTransportContext & {
      readonly stage: 'delivered_too_late';
      readonly frameAgeMs: number;
    })
  | (CanvasAgentTransportContext & {
      readonly stage: 'transport_unavailable';
      readonly executionState: 'completed';
      readonly envelopeOutcome: 'success' | 'failure';
      readonly durationMs: number;
    })
  /**
   * L'éditeur a refusé au moins une écriture de cette action parce qu'un export
   * était en vol (#1860). Quand le réessai l'a surmonté (`recovered`), c'est la
   * SEULE trace du refus : le reçu rendu à l'agent n'en dit rien.
   */
  | (CanvasAgentTransportContext & {
      readonly stage: 'transient_refusal_settled';
      readonly outcome: IssueDesRefusPassagers;
      readonly refusalCount: number;
      readonly waitedMs: number;
    })
  /**
   * `mutations.apply` a levé sur un plan d'`apply_edit_plan`. `refused` : la révision relue n'a pas bougé, rien n'est
   * écrit. `unconfirmed` : elle a bougé ou n'a pas pu être relue. Seul le NOM de l'erreur part, sous la forme d'un
   * code : son message peut citer le texte de l'acte.
   */
  | (CanvasAgentTransportContext & {
      readonly stage: 'edit_plan_apply_threw';
      readonly outcome: 'refused' | 'unconfirmed';
      readonly errorName: string;
    });

function agentTransportPropertiesFor(event: CanvasAgentTransportDiagnostic): PostHogProperties {
  const properties: PostHogProperties = {
    schema_version: 1,
    feature: 'redaction_web_canvas',
    stage: event.stage,
    request_id: event.requestId,
    tool: event.tool,
    action: event.action,
    ...(event.mountEpoch !== undefined ? { mount_epoch: event.mountEpoch } : {}),
  };
  if (event.stage === 'response_send_attempted') {
    properties.envelope_outcome = event.envelopeOutcome;
    properties.duration_ms = event.durationMs;
  } else if (event.stage === 'delivered_too_late') {
    properties.frame_age_ms = event.frameAgeMs;
  } else if (event.stage === 'transport_unavailable') {
    properties.execution_state = event.executionState;
    properties.envelope_outcome = event.envelopeOutcome;
    properties.duration_ms = event.durationMs;
  } else if (event.stage === 'transient_refusal_settled') {
    properties.outcome = event.outcome;
    properties.refusal_count = event.refusalCount;
    properties.waited_ms = event.waitedMs;
  } else if (event.stage === 'edit_plan_apply_threw') {
    properties.outcome = event.outcome;
    properties.error_name = codeOuNull(event.errorName);
  }
  return properties;
}

/**
 * Corrèle la réception et la fin d'une opération Canvas agent sans copier les
 * arguments, receipts, sélecteurs ou contenu juridique dans l'analytics. Un
 * envoi tenté ne vaut pas accusé de réception backend.
 */
export function captureCanvasAgentTransportDiagnostic(event: CanvasAgentTransportDiagnostic): void {
  const properties = agentTransportPropertiesFor(event);
  try {
    posthog.capture(AGENT_TRANSPORT_EVENT_NAME, properties);
  } catch {
    // Une panne analytique ne doit jamais modifier l'exécution de l'outil Canvas.
  }
  try {
    console.info(AGENT_TRANSPORT_LOG_PREFIX, properties);
  } catch {
    // Les WebViews peuvent remplacer console par une façade indisponible.
  }
}

function common(event: CanvasWriteDiagnostic): PostHogProperties {
  return {
    schema_version: 1,
    feature: 'redaction_web_canvas',
    stage: event.stage,
    conversation_id: event.conversationId,
    document_id: event.documentId,
    job_id: event.jobId,
    mount_epoch: event.mountEpoch,
  };
}

function propertiesFor(event: CanvasWriteDiagnostic): PostHogProperties {
  const base = common(event);
  switch (event.stage) {
    case 'chunk_started':
      return {
        ...base,
        operation_id: event.operationId,
        ...(event.requestId === undefined ? {} : { request_id: event.requestId }),
        write_generation: event.writeGeneration,
        chunk_index: event.chunkIndex,
        chunk_count: event.chunkCount,
        chunk_block_count: event.chunkBlockCount,
        attempt_index: event.attemptIndex,
        cursor_emitted: event.cursorEmitted,
        cursor_confirmed: event.cursorConfirmed,
      };
    case 'chunk_completed':
    case 'chunk_timed_out':
      return {
        ...base,
        operation_id: event.operationId,
        ...(event.requestId === undefined ? {} : { request_id: event.requestId }),
        write_generation: event.writeGeneration,
        chunk_index: event.chunkIndex,
        chunk_count: event.chunkCount,
        chunk_block_count: event.chunkBlockCount,
        attempt_index: event.attemptIndex,
        cursor_emitted: event.cursorEmitted,
        cursor_confirmed: event.cursorConfirmed,
        duration_ms: event.durationMs,
      };
    case 'transient_retry':
      return {
        ...base,
        operation_id: event.operationId,
        ...(event.requestId === undefined ? {} : { request_id: event.requestId }),
        write_generation: event.writeGeneration,
        chunk_index: event.chunkIndex,
        chunk_count: event.chunkCount,
        chunk_block_count: event.chunkBlockCount,
        attempt_index: event.attemptIndex,
        cursor_emitted: event.cursorEmitted,
        cursor_confirmed: event.cursorConfirmed,
        provider_reason: event.providerReason,
        retry_delay_ms: event.retryDelayMs,
      };
    case 'writer_export_wait_started':
      return {
        ...base,
        ...(event.requestId === undefined ? {} : { request_id: event.requestId }),
      };
    case 'writer_export_wait_completed':
      return {
        ...base,
        ...(event.requestId === undefined ? {} : { request_id: event.requestId }),
        duration_ms: event.durationMs,
      };
    case 'stream_completed':
      return {
        ...base,
        stream_block_count: event.streamBlockCount,
        cursor_emitted: event.cursorEmitted,
        cursor_confirmed: event.cursorConfirmed,
        write_in_flight: event.writeInFlight,
      };
    case 'render_integrity':
      return {
        ...base,
        sample_phase: event.samplePhase,
        pagination_stable: event.paginationStable,
        sample_count: event.sampleCount,
        model_block_count: event.modelBlockCount,
        painted_page_count: event.paintedPageCount,
        painted_fragment_count: event.paintedFragmentCount,
        painted_unique_block_count: event.paintedUniqueBlockCount,
        duplicate_painted_fragment_count: event.duplicatePaintedFragmentCount,
        anomaly_kind: event.anomalyKind,
      };
    case 'superdoc_error':
      return {
        ...base,
        error_source: event.errorSource,
        payload_kind: event.signature.payloadKind,
        diagnostic_code: event.signature.diagnosticCode,
        diagnostic_stage: event.signature.diagnosticStage,
        severity: event.signature.severity,
        internal_code: event.signature.internalCode,
      };
  }
}

/**
 * Capture un diagnostic requêtable et visible dans la console du Session
 * Replay. Une panne d'observabilité ne doit jamais devenir une panne d'édition.
 */
export function captureCanvasWriteDiagnostic(event: CanvasWriteDiagnostic): void {
  const properties = propertiesFor(event);
  try {
    posthog.capture(EVENT_NAME, properties);
  } catch {
    // L'observabilité est best-effort ; l'écriture du document reste prioritaire.
  }
  try {
    // PostHog enregistre les logs console dans le Session Replay. Le préfixe
    // permet une recherche directe sans confondre ces observations avec une
    // exception applicative.
    console.info(LOG_PREFIX, properties);
  } catch {
    // Certains navigateurs embarqués peuvent remplacer console par une façade.
  }
}

export interface CanvasPaintFragment {
  /** Utilisé localement pour compter ; n'est jamais rendu par l'analyse. */
  readonly blockId: string;
  /** Utilisé localement pour distinguer un split d'un clone ; jamais capturé. */
  readonly normalizedText: string;
}

export type CanvasPaintAnomaly =
  | 'duplicate_painted_fragment'
  | 'model_present_paint_empty'
  | null;

export interface CanvasPaintDiagnostic {
  readonly modelBlockCount: number | null;
  readonly paintedPageCount: number;
  readonly paintedFragmentCount: number;
  readonly paintedUniqueBlockCount: number;
  readonly duplicatePaintedFragmentCount: number;
  readonly anomalyKind: CanvasPaintAnomaly;
}

export interface CanvasStablePaintDiagnostic {
  readonly diagnostic: CanvasPaintDiagnostic;
  readonly paginationStable: boolean;
  readonly sampleCount: number;
}

/** Réduit modèle + fragments peints en compteurs dépourvus de contenu. */
export function analyserPeintureDuCanvas(input: {
  readonly modelBlockCount: number | null;
  readonly pageCount: number;
  readonly fragments: readonly CanvasPaintFragment[];
}): CanvasPaintDiagnostic {
  const uniques = new Set(input.fragments.map((fragment) => fragment.blockId));
  const occurrences = new Map<string, number>();
  let duplicates = 0;
  for (const fragment of input.fragments) {
    const cleLocale = `${fragment.blockId}\u0000${fragment.normalizedText}`;
    const dejaVus = occurrences.get(cleLocale) ?? 0;
    if (dejaVus > 0) duplicates += 1;
    occurrences.set(cleLocale, dejaVus + 1);
  }

  const anomalyKind: CanvasPaintAnomaly = duplicates > 0
    ? 'duplicate_painted_fragment'
    : (input.modelBlockCount ?? 0) > 0 && input.fragments.length === 0
      ? 'model_present_paint_empty'
      : null;

  return {
    modelBlockCount: input.modelBlockCount,
    paintedPageCount: input.pageCount,
    paintedFragmentCount: input.fragments.length,
    paintedUniqueBlockCount: uniques.size,
    duplicatePaintedFragmentCount: duplicates,
    anomalyKind,
  };
}

/**
 * Mesure la seule surface vue par l'avocat, bornée au scroll-container du
 * montage. Les ancêtres portant le même `data-block-id` sont écartés : seul le
 * fragment le plus profond représente une unité peinte.
 */
export function mesurerPeintureDuCanvas(
  root: ParentNode | null,
  modelBlockCount: number | null,
): CanvasPaintDiagnostic {
  if (!root) {
    return analyserPeintureDuCanvas({ modelBlockCount, pageCount: 0, fragments: [] });
  }
  const candidates = [...root.querySelectorAll<HTMLElement>('[data-block-id]')];
  const candidateSet = new Set(candidates);
  const avecDescendantCandidat = new Set<HTMLElement>();
  for (const element of candidates) {
    let ancestor = element.parentElement;
    while (ancestor && root.contains(ancestor)) {
      if (candidateSet.has(ancestor)) avecDescendantCandidat.add(ancestor);
      ancestor = ancestor.parentElement;
    }
  }
  const deepest = candidates.filter((element) => !avecDescendantCandidat.has(element));
  const fragments = deepest
    .filter((element) => !element.getAttribute('data-layout-story')?.startsWith('footer:'))
    .map((element) => ({
      blockId: element.dataset.blockId ?? '',
      normalizedText: (element.textContent ?? '').replace(/\s+/g, ' ').trim(),
    }))
    .filter((fragment) => fragment.blockId.length > 0 && fragment.normalizedText.length > 0);

  return analyserPeintureDuCanvas({
    modelBlockCount,
    pageCount: root.querySelectorAll('.superdoc-page').length,
    fragments,
  });
}

/**
 * Attend une photographie peinte stable, car SuperDoc repagine sans signal de
 * fin. Le plafond garde cette sonde best-effort : elle ne doit jamais retenir
 * l'enregistrement ni vivre indéfiniment dans un onglet ralenti.
 */
export async function attendrePeintureStableDuCanvas(input: {
  readonly root: ParentNode | null;
  readonly modelBlockCount: number | null;
  /** Injection de mesure réservée aux tests du protocole de stabilisation. */
  readonly mesurer?: () => CanvasPaintDiagnostic;
  readonly attendre?: (delayMs: number) => Promise<void>;
  readonly toursStables?: number;
  readonly tourMs?: number;
  readonly toursMax?: number;
}): Promise<CanvasStablePaintDiagnostic> {
  const attendre = input.attendre ?? ((delayMs: number) => new Promise<void>(
    (resolve) => globalThis.setTimeout(resolve, delayMs),
  ));
  const mesurer = input.mesurer ?? (() => mesurerPeintureDuCanvas(
    input.root,
    input.modelBlockCount,
  ));
  const toursStables = input.toursStables ?? 3;
  const tourMs = input.tourMs ?? 500;
  const toursMax = input.toursMax ?? 12;
  let diagnostic = mesurer();
  let signaturePrecedente = JSON.stringify(diagnostic);
  let stabilitesConsecutives = 0;
  let sampleCount = 1;

  for (let tour = 1; tour < toursMax; tour += 1) {
    await attendre(tourMs);
    diagnostic = mesurer();
    sampleCount += 1;
    const signature = JSON.stringify(diagnostic);
    stabilitesConsecutives = signature === signaturePrecedente
      ? stabilitesConsecutives + 1
      : 0;
    signaturePrecedente = signature;
    if (stabilitesConsecutives >= toursStables) {
      return { diagnostic, paginationStable: true, sampleCount };
    }
  }

  return { diagnostic, paginationStable: false, sampleCount };
}
